/** Dev overlay: presentation FPS + main-thread CPU time (JS physics/draw; GPU not included). */

export const DEV_FPS_UPDATE_INTERVAL_MS = 250;
export const DEV_FPS_CPU_WINDOW_MS = 5000;
export const DEV_FPS_MAX_CPU_MS = 200;

/**
 * @param {number} ms
 * @returns {string}
 */
export function formatDevFrameMs(ms) {
  if (!isFinite(ms) || ms <= 0) return "—";
  if (ms >= 100) return ms.toFixed(0);
  if (ms >= 10) return ms.toFixed(1);
  return ms.toFixed(2);
}

/**
 * @param {number[]} values
 * @param {number} p 0–100
 * @returns {number | null}
 */
export function percentile(values, p) {
  if (!values || values.length === 0) return null;
  var sorted = values.slice().sort(function (a, b) { return a - b; });
  var idx = Math.ceil((p / 100) * sorted.length) - 1;
  idx = Math.max(0, Math.min(sorted.length - 1, idx));
  return sorted[idx];
}

/**
 * @param {{
 *   updateIntervalMs?: number,
 *   cpuWindowMs?: number,
 *   maxCpuMs?: number,
 * }} [options]
 */
export function createDevFpsTracker(options) {
  options = options || {};
  var updateIntervalMs = options.updateIntervalMs != null
    ? options.updateIntervalMs
    : DEV_FPS_UPDATE_INTERVAL_MS;
  var cpuWindowMs = options.cpuWindowMs != null
    ? options.cpuWindowMs
    : DEV_FPS_CPU_WINDOW_MS;
  var maxCpuMs = options.maxCpuMs != null
    ? options.maxCpuMs
    : DEV_FPS_MAX_CPU_MS;

  /** @type {{ at: number, cpu: number, sky?: number, phys?: number, draw?: number }[]} */
  var cpuSamples = [];
  var shortAcc = { frames: 0, since: null };
  /** @type {number | null} */
  var lastDisplayAt = null;

  function pruneCpuSamples(now) {
    var cutoff = now - cpuWindowMs;
    while (cpuSamples.length > 0 && cpuSamples[0].at < cutoff) {
      cpuSamples.shift();
    }
  }

  function averageField(field) {
    var sum = 0;
    var count = 0;
    for (var i = 0; i < cpuSamples.length; i++) {
      var value = cpuSamples[i][field];
      if (value != null && isFinite(value) && value > 0) {
        sum += value;
        count += 1;
      }
    }
    return count > 0 ? sum / count : null;
  }

  function reset() {
    cpuSamples = [];
    shortAcc = { frames: 0, since: null };
    lastDisplayAt = null;
  }

  /**
   * @param {number} now
   * @param {number} [cpuMs] main-thread time inside the frame callback
   * @param {{ sky?: number, phys?: number, draw?: number }} [phases]
   * @returns {{ text: string, fps: number, avgCpuMs: number | null, p95CpuMs: number | null } | null}
   */
  function tick(now, cpuMs, phases) {
    if (!isFinite(now)) return null;

    if (cpuMs != null && isFinite(cpuMs) && cpuMs > 0 && cpuMs < maxCpuMs) {
      var sample = { at: now, cpu: cpuMs };
      if (phases) {
        if (phases.sky != null && phases.sky > 0) sample.sky = phases.sky;
        if (phases.phys != null && phases.phys > 0) sample.phys = phases.phys;
        if (phases.draw != null && phases.draw > 0) sample.draw = phases.draw;
      }
      cpuSamples.push(sample);
    }
    pruneCpuSamples(now);

    if (shortAcc.since == null) shortAcc.since = now;
    shortAcc.frames += 1;

    var sinceDisplay = lastDisplayAt != null ? now - lastDisplayAt : now - shortAcc.since;
    if (sinceDisplay < updateIntervalMs) return null;
    lastDisplayAt = now;

    var shortElapsed = now - shortAcc.since;
    var fps = shortElapsed > 0 ? Math.round((shortAcc.frames / shortElapsed) * 1000) : 0;
    shortAcc.frames = 0;
    shortAcc.since = now;

    var avgCpuMs = averageField("cpu");
    var p95CpuMs = percentile(cpuSamples.map(function (s) { return s.cpu; }), 95);
    var avgSky = averageField("sky");
    var avgPhys = averageField("phys");
    var avgDraw = averageField("draw");

    var text = String(fps) + " fps";
    if (avgCpuMs != null && avgCpuMs > 0) {
      text += " · " + formatDevFrameMs(avgCpuMs) + " ms cpu";
      if (p95CpuMs != null && p95CpuMs > 0) {
        text += " (p95 " + formatDevFrameMs(p95CpuMs) + ")";
      }
    }
    if (avgSky != null || avgPhys != null || avgDraw != null) {
      var parts = [];
      if (avgSky != null) parts.push("sky " + formatDevFrameMs(avgSky));
      if (avgPhys != null) parts.push("phys " + formatDevFrameMs(avgPhys));
      if (avgDraw != null) parts.push("draw " + formatDevFrameMs(avgDraw));
      if (parts.length > 0) text += "\n" + parts.join(" · ");
    }
    return {
      text: text,
      fps: fps,
      avgCpuMs: avgCpuMs,
      p95CpuMs: p95CpuMs,
    };
  }

  return { tick: tick, reset: reset };
}